import { useState } from 'react';
import { Database, Users, Settings, Zap, CheckCircle, XCircle, AlertCircle, Play, RotateCcw, Download, Activity } from 'lucide-react';
import { tenantConfigurationService } from '../../../services/tenantConfigurationService';
import Icon from '../../../components/AppIcon';


export default function BulkOperations({ organizations, onOperationComplete }) {
  const [selectedOperation, setSelectedOperation] = useState('');
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [operationParams, setOperationParams] = useState({});
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResults, setExecutionResults] = useState([]);
  const [error, setError] = useState('');

  const bulkOperations = [
    {
      id: 'feature_rollout',
      name: 'Feature Rollout',
      description: 'Enable/disable features across multiple organizations',
      icon: Zap,
      color: 'text-blue-600 bg-blue-50',
      params: [
        { key: 'featureFlag', type: 'text', label: 'Feature Flag Name', required: true },
        { key: 'value', type: 'select', label: 'Value', options: ['true', 'false'], required: true }
      ]
    },
    {
      id: 'subscription_update',
      name: 'Subscription Update',
      description: 'Update subscription plans for multiple organizations',
      icon: Settings,
      color: 'text-green-600 bg-green-50',
      params: [
        { key: 'planName', type: 'select', label: 'Plan', options: ['starter', 'professional', 'enterprise'], required: true },
        { key: 'priceAmount', type: 'number', label: 'Price (cents)', required: true }
      ]
    },
    {
      id: 'user_management',
      name: 'User Management',
      description: 'Add or remove users across organizations',
      icon: Users,
      color: 'text-purple-600 bg-purple-50',
      params: [
        { key: 'action', type: 'select', label: 'Action', options: ['add', 'remove', 'update_role'], required: true },
        { key: 'userEmail', type: 'email', label: 'User Email', required: true },
        { key: 'role', type: 'select', label: 'Role', options: ['org_admin', 'org_user', 'org_viewer'] }
      ]
    },
    {
      id: 'maintenance_mode',
      name: 'Maintenance Mode',
      description: 'Enable/disable maintenance mode for organizations',
      icon: Activity,
      color: 'text-orange-600 bg-orange-50',
      params: [
        { key: 'enabled', type: 'select', label: 'Maintenance Mode', options: ['true', 'false'], required: true },
        { key: 'message', type: 'textarea', label: 'Maintenance Message' }
      ]
    },
    {
      id: 'data_export',
      name: 'Data Export',
      description: 'Export organization data in bulk',
      icon: Download,
      color: 'text-indigo-600 bg-indigo-50',
      params: [
        { key: 'format', type: 'select', label: 'Export Format', options: ['csv', 'json', 'xlsx'], required: true },
        { key: 'includeUsers', type: 'select', label: 'Include Users', options: ['true', 'false'] }
      ]
    }
  ];

  const handleOperationSelect = (operationId) => {
    setSelectedOperation(operationId);
    setOperationParams({});
    setExecutionResults([]);
    setError('');
  };

  const handleOrganizationToggle = (orgId) => {
    setSelectedOrganizations(prev => 
      prev?.includes(orgId) 
        ? prev?.filter(id => id !== orgId)
        : [...prev, orgId]
    );
  };

  const handleSelectAll = () => {
    setSelectedOrganizations(
      selectedOrganizations?.length === organizations?.length 
        ? [] 
        : organizations?.map(org => org?.id)
    );
  };

  const handleParamChange = (key, value) => {
    setOperationParams(prev => ({ ...prev, [key]: value }));
  };

  const validateOperation = () => {
    if (!selectedOperation) return 'Please select an operation';
    if (selectedOrganizations?.length === 0) return 'Please select at least one organization';
    
    const operation = bulkOperations?.find(op => op?.id === selectedOperation);
    const requiredParams = operation?.params?.filter(param => param?.required);
    
    for (const param of requiredParams) {
      if (!operationParams?.[param?.key]) {
        return `Please provide a value for ${param?.label}`;
      }
    }
    
    return null;
  };

  const executeOperation = async () => {
    const validationError = validateOperation();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsExecuting(true);
    setError('');
    setExecutionResults([]);

    try {
      const results = [];

      for (const orgId of selectedOrganizations) {
        const org = organizations?.find(o => o?.id === orgId);
        
        try {
          let result = null;
          
          switch (selectedOperation) {
            case 'feature_rollout':
              // Find the feature flag ID (mock implementation)
              result = await tenantConfigurationService?.updateOrganizationFeatureFlag(
                orgId,
                'mock-feature-id', // In real implementation, you'd look up the ID by name
                operationParams?.value
              );
              break;
              
            case 'subscription_update':
              result = await tenantConfigurationService?.updateSubscription(orgId, {
                planName: operationParams?.planName,
                priceAmount: parseInt(operationParams?.priceAmount),
                currency: 'USD'
              });
              break;
              
            case 'user_management':
              if (operationParams?.action === 'add') {
                result = await tenantConfigurationService?.addOrganizationMember(
                  orgId,
                  operationParams?.userEmail,
                  operationParams?.role || 'org_user'
                );
              }
              // Other user management actions would be implemented here
              break;
              
            case 'maintenance_mode':
              result = await tenantConfigurationService?.updateOrganization(orgId, {
                settings: {
                  maintenanceMode: operationParams?.enabled === 'true',
                  maintenanceMessage: operationParams?.message || 'System maintenance in progress'
                }
              });
              break;
              
            case 'data_export':
              // Mock data export - in real implementation, this would generate export files
              result = {
                success: true,
                exportUrl: `https://example.com/exports/${orgId}-${Date.now()}.${operationParams?.format}`,
                recordCount: Math.floor(Math.random() * 1000) + 100
              };
              break;
              
            default:
              throw new Error('Unknown operation');
          }

          results?.push({
            organizationId: orgId,
            organizationName: org?.name,
            status: 'success',
            result: result,
            message: 'Operation completed successfully'
          });
        } catch (err) {
          results?.push({
            organizationId: orgId,
            organizationName: org?.name,
            status: 'error',
            error: err?.message,
            message: `Failed: ${err?.message}`
          });
        }
      }

      setExecutionResults(results);
      
      // Call parent refresh if any operations succeeded
      if (results?.some(r => r?.status === 'success')) {
        onOperationComplete();
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setIsExecuting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-50';
      case 'error': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const resetOperation = () => {
    setSelectedOperation('');
    setSelectedOrganizations([]);
    setOperationParams({});
    setExecutionResults([]);
    setError('');
  };

  const exportResults = () => {
    const data = executionResults?.map(result => ({
      organization: result?.organizationName,
      status: result?.status,
      message: result?.message
    }));
    
    const csv = [
      'Organization,Status,Message',
      ...data?.map(row => `"${row?.organization}","${row?.status}","${row?.message}"`)
    ]?.join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-operation-results-${Date.now()}.csv`;
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Bulk Operations</h2>
          <p className="text-gray-600">
            Perform mass operations across multiple tenant organizations
          </p>
        </div>
        <button
          onClick={resetOperation}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
        >
          <RotateCcw className="h-4 w-4" />
          <span>Reset</span>
        </button>
      </div>
      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Operation Selection */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Operation</h3>
            
            <div className="space-y-3">
              {bulkOperations?.map((operation) => {
                const Icon = operation?.icon;
                return (
                  <div
                    key={operation?.id}
                    onClick={() => handleOperationSelect(operation?.id)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedOperation === operation?.id
                        ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className={`p-2 rounded-lg ${operation?.color} mr-3`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{operation?.name}</h4>
                        <p className="text-sm text-gray-600 mt-1">{operation?.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Organization Selection */}
          <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Select Organizations</h3>
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {selectedOrganizations?.length === organizations?.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            
            <div className="max-h-64 overflow-y-auto space-y-2">
              {organizations?.map((org) => (
                <label key={org?.id} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedOrganizations?.includes(org?.id)}
                    onChange={() => handleOrganizationToggle(org?.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div className="ml-3 flex-1">
                    <div className="text-sm font-medium text-gray-900">{org?.name}</div>
                    <div className="text-xs text-gray-500">{org?.members?.length} members</div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    org?.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {org?.status}
                  </span>
                </label>
              ))}
            </div>
            
            <div className="mt-4 text-sm text-gray-600">
              {selectedOrganizations?.length} of {organizations?.length} organizations selected
            </div>
          </div>
        </div>

        {/* Operation Configuration & Execution */}
        <div className="lg:col-span-2">
          {selectedOperation ? (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Configure Operation: {bulkOperations?.find(op => op?.id === selectedOperation)?.name}
              </h3>

              {/* Operation Parameters */}
              <div className="space-y-4 mb-6">
                {bulkOperations?.find(op => op?.id === selectedOperation)?.params?.map((param) => (
                  <div key={param?.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {param?.label} {param?.required && <span className="text-red-500">*</span>}
                    </label>
                    
                    {param?.type === 'select' ? (
                      <select
                        value={operationParams?.[param?.key] || ''}
                        onChange={(e) => handleParamChange(param?.key, e?.target?.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select {param?.label}</option>
                        {param?.options?.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    ) : param?.type === 'textarea' ? (
                      <textarea
                        value={operationParams?.[param?.key] || ''}
                        onChange={(e) => handleParamChange(param?.key, e?.target?.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={`Enter ${param?.label}`}
                      />
                    ) : (
                      <input
                        type={param?.type}
                        value={operationParams?.[param?.key] || ''}
                        onChange={(e) => handleParamChange(param?.key, e?.target?.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={`Enter ${param?.label}`}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Execution Button */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Operation will be applied to {selectedOrganizations?.length} organization{selectedOrganizations?.length !== 1 ? 's' : ''}
                </div>
                <button
                  onClick={executeOperation}
                  disabled={isExecuting || selectedOrganizations?.length === 0}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isExecuting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Executing...</span>
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      <span>Execute Operation</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
              <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select an Operation</h3>
              <p className="text-gray-600">Choose a bulk operation from the list to get started</p>
            </div>
          )}

          {/* Execution Results */}
          {executionResults?.length > 0 && (
            <div className="mt-8 bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Execution Results</h3>
                <button
                  onClick={exportResults}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>Export Results</span>
                </button>
              </div>

              <div className="space-y-3">
                {executionResults?.map((result, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {result?.status === 'success' ? (
                          <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-600 mr-3" />
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{result?.organizationName}</p>
                          <p className="text-sm text-gray-600">{result?.message}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(result?.status)}`}>
                        {result?.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>
                    Total: {executionResults?.length} operations
                  </span>
                  <div className="flex items-center space-x-4">
                    <span className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-1" />
                      {executionResults?.filter(r => r?.status === 'success')?.length} successful
                    </span>
                    <span className="flex items-center">
                      <XCircle className="h-4 w-4 text-red-600 mr-1" />
                      {executionResults?.filter(r => r?.status === 'error')?.length} failed
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}